import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { requestOTP, verifyOTP } from "../lib/api";

const CHANNELS = [
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
];

// Server enforces the same value in backend/app/otp.py::REQUEST_COOLDOWN_SECONDS. Keep in sync
// or the "Resend" button will 429 the moment the timer hits zero.
const RESEND_COOLDOWN_SECONDS = 30;

/**
 * OTP-only login (CLAUDE.md "Auth model") — no password. Email (Task 8) and WhatsApp via WATI
 * (Task 9) share the same two-step flow: request a code, then verify it.
 */
export function LoginPage() {
  const [channel, setChannel] = useState("email");
  const [step, setStep] = useState("request");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState(); // E.164, from PhoneInput
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [resending, setResending] = useState(false);
  const intervalRef = useRef(null);
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const identifier = channel === "email" ? email.trim() : mobile;

  // Whenever the user lands on the verify step (initial send or a successful resend), start a
  // 30-second countdown. Interval is cleared on unmount and when the timer reaches zero.
  function startResendCountdown() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setResendSeconds(RESEND_COOLDOWN_SECONDS);
    intervalRef.current = setInterval(() => {
      setResendSeconds((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  function switchChannel(next) {
    setChannel(next);
    setStep("request");
    setCode("");
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setResendSeconds(0);
  }

  async function handleRequest(e) {
    e.preventDefault();
    if (!identifier) return;
    setSubmitting(true);
    try {
      await requestOTP(channel, identifier);
      setCode("");
      setStep("verify");
      startResendCountdown();
      toast.success(channel === "email" ? "Code sent — check your email." : "Code sent on WhatsApp.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send a code. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (resendSeconds > 0 || resending || !identifier) return;
    setResending(true);
    try {
      await requestOTP(channel, identifier);
      setCode("");
      startResendCountdown();
      toast.success(channel === "email" ? "New code sent to your email." : "New code sent on WhatsApp.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't resend the code. Try again.");
    } finally {
      setResending(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await verifyOTP(channel, identifier, code.trim());
      await refresh();
      navigate(location.state?.from?.pathname || "/app", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Incorrect or expired code.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-sm flex-col justify-center px-4 py-16">
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="text-gold">The Money</span> Gurukul
          </CardTitle>
          <CardDescription>
            {step === "request"
              ? `Get a one-time login code by ${channel === "email" ? "email" : "WhatsApp"}.`
              : `Enter the code we sent to ${identifier}.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === "request" ? (
            <form onSubmit={handleRequest} className="flex flex-col gap-4">
              <div className="flex rounded-md border border-border p-1">
                {CHANNELS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => switchChannel(c.value)}
                    className={`flex-1 rounded-sm py-1.5 text-sm font-medium transition-colors ${
                      channel === c.value ? "bg-gold text-on-gold" : "text-muted hover:text-text"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              {channel === "email" ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="mobile">WhatsApp number</Label>
                  <PhoneInput
                    id="mobile"
                    international
                    defaultCountry="IN"
                    value={mobile}
                    onChange={setMobile}
                    inputComponent={Input}
                    className="phone-input-theme"
                  />
                </div>
              )}

              <Button type="submit" disabled={submitting || !identifier}>
                {submitting ? "Sending…" : "Send code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="code">6-digit code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  required
                  data-testid="otp-code-input"
                />
              </div>
              <Button type="submit" disabled={submitting} data-testid="otp-verify-button">
                {submitting ? "Verifying…" : "Verify & log in"}
              </Button>
              <div className="flex items-center justify-center gap-2 text-xs">
                {resendSeconds > 0 ? (
                  <span className="text-muted" data-testid="otp-resend-timer">
                    Resend code in <span className="font-mono text-text">{resendSeconds}s</span>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resending}
                    className="font-medium text-gold underline-offset-2 hover:underline disabled:opacity-50"
                    data-testid="otp-resend-button"
                  >
                    {resending ? "Resending…" : "Resend code"}
                  </button>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep("request")}
                data-testid="otp-change-target-button"
              >
                Use a different {channel === "email" ? "email" : "number"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <p className="mt-4 text-center text-xs text-muted">
        Access is for enrolled The Money Gurukul members.
      </p>
    </div>
  );
}
